import React from 'react';
import { View, Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ActivityIndicator, Surface, Text, useTheme } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

export interface LoadingScreenProps {
  /**
   * Loading message to display below the spinner
   */
  message?: string;
  
  /**
   * Whether to show the full screen loading overlay (with SafeAreaView)
   * Set to false if you want to use it as a centered view inside another component
   */
  fullScreen?: boolean;
  
  /**
   * Size of the activity indicator: 'small' | 'large'
   * @default 'large'
   */
  size?: 'small' | 'large';
  
  /**
   * Custom style for the container
   */
  containerStyle?: any;
  
  /**
   * Custom style for the message text
   */
  messageStyle?: any;
}

/**
 * Beautiful, centralized loading screen component
 * Respects user theme from React Native Paper
 * 
 * Usage:
 * - Ecrã inteiro: <LoadingScreen message={mensagemTraduzida} />
 * - Vista centrada: <LoadingScreen fullScreen={false} message={mensagemTraduzida} />
 */
export const LoadingScreen = React.memo(({
  message,
  fullScreen = true,
  size = 'large',
  containerStyle,
  messageStyle,
}: LoadingScreenProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const resolvedMessage = message ?? t('loading.default');

  const content = (
    <View
      style={[
        styles.centerContainer,
        {
          backgroundColor: theme.colors.background,
        },
        containerStyle,
      ]}
    >
      <ActivityIndicator
        size={size}
        color={theme.colors.primary}
        style={styles.spinner}
      />
      {resolvedMessage && (
        <Text
          variant="bodyLarge"
          style={[
            styles.message,
            {
              color: theme.colors.onBackground,
            },
            messageStyle,
          ]}
        >
          {resolvedMessage}
        </Text>
      )}
    </View>
  );

  if (!fullScreen) {
    return content;
  }

  // Full screen with SafeAreaView and header support
  return (
    <SafeAreaView
      style={[
        styles.fullScreenContainer,
        {
          backgroundColor: theme.colors.background,
          paddingTop: Platform.OS === 'ios' ? insets.top : 0,
        },
      ]}
    >
      <Surface
        style={[
          styles.surface,
          {
            backgroundColor: theme.colors.background,
          },
        ]}
      >
        {content}
      </Surface>
    </SafeAreaView>
  );
});

LoadingScreen.displayName = 'LoadingScreen';

const styles = StyleSheet.create({
  fullScreenContainer: {
    flex: 1,
  },
  surface: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  spinner: {
    marginBottom: 16,
  },
  message: {
    textAlign: 'center',
    marginTop: 8,
  },
});
